// Growth › Platforms: /growth/platforms?kind=&bucket=&target=&public_pages=
//
// What leagues use instead of ScoreLM: what each costs, how it compares, and how many
// leads are on it. The filters live in the URL, as on Leads. There's no paging: it's a
// few dozen rows.

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { Platform } from '@/api/growthPlatforms'
import { BUCKETS, PLATFORM_KINDS, PUBLIC_PAGES, type Bucket, type PlatformKind, type PublicPages } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import { Badge, Button, Card, DataTable, EmptyState, ErrorState, SelectInput, SkeletonRows, useToast, type Column } from '@/components/ui'
import { BUCKET_LABELS, PLATFORM_KIND_LABELS, platformPrice, PUBLIC_PAGES_LABELS } from '@/lib/growth'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { BaselineCard, BucketBadge, PriceVsBadge, VsBadge } from './platformShared'

const KIND_FILTERS = [{ value: '', label: 'Any kind' }, ...PLATFORM_KINDS.map((k) => ({ value: k, label: PLATFORM_KIND_LABELS[k] }))]
const TARGET_FILTERS = [
  { value: '', label: 'Target or not' },
  { value: 'true', label: 'Target' },
  { value: 'false', label: 'Not a target' },
]
const PUBLIC_FILTERS = [{ value: '', label: 'Any public pages' }, ...PUBLIC_PAGES.map((p) => ({ value: p, label: PUBLIC_PAGES_LABELS[p] }))]

function readParams(p: URLSearchParams) {
  const kind = p.get('kind')
  const bucket = p.get('bucket')
  const target = p.get('target')
  const pages = p.get('public_pages')
  return {
    kind: (PLATFORM_KINDS as readonly string[]).includes(kind ?? '') ? (kind as PlatformKind) : undefined,
    bucket: (BUCKETS as readonly string[]).includes(bucket ?? '') ? (bucket as Bucket) : undefined,
    target: target === 'true' ? true : target === 'false' ? false : undefined,
    public_pages: (PUBLIC_PAGES as readonly string[]).includes(pages ?? '') ? (pages as PublicPages) : undefined,
  }
}

function PlatformTitle({ platform: p }: { platform: Platform }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="break-words font-semibold">{p.name}</span>
      <span className="flex flex-wrap items-center gap-1 text-xs font-medium text-muted">
        {PLATFORM_KIND_LABELS[p.kind]}
        {p.verified ? null : <Badge tone="outline">Unverified</Badge>}
        {p.is_active ? null : <Badge tone="outline">Closed</Badge>}
      </span>
    </span>
  )
}

function LoadButton({ variant }: { variant?: 'primary' }) {
  const toast = useToast()
  const [pending, setPending] = useState(false)
  const load = async () => {
    setPending(true)
    try {
      const r = await api.growth.platforms.seed()
      invalidateQueries('growth')
      toast.success(`${r.total} platforms: ${r.inserted} added, ${r.updated} changed. ${r.leads_linked} leads linked.`)
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setPending(false)
    }
  }
  return (
    <Button variant={variant} loading={pending} onClick={() => void load()}>
      Load the platform list
    </Button>
  )
}

export default function PlatformsListPage() {
  useDocumentTitle('Platforms')
  const [searchParams, setSearchParams] = useSearchParams()
  const { kind, bucket, target, public_pages } = readParams(searchParams)

  const update = (patch: Record<string, string | boolean | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '') next.delete(k)
          else next.set(k, String(v))
        }
        return next
      },
      { replace: true },
    )

  const params = useMemo(() => ({ kind, bucket, target, public_pages }), [kind, bucket, target, public_pages])
  const { data, error, status, isFetching, refetch } = useQuery(['growth', 'platforms', params], (signal) => api.growth.platforms.list(params, signal), { keepPrevious: true })
  const rows = data?.platforms ?? []
  const counts = data?.bucket_counts
  const hasFilters = !!kind || !!bucket || target !== undefined || !!public_pages

  const columns: Column<Platform>[] = [
    { id: 'name', header: 'Platform', primary: true, cell: (p) => <PlatformTitle platform={p} />, className: 'min-w-[12rem] max-w-[18rem]' },
    { id: 'bucket', header: 'Bucket', cell: (p) => <BucketBadge bucket={p.bucket} /> },
    { id: 'price', header: 'Price', cell: (p) => <span className="text-muted">{platformPrice(p)}</span>, className: 'max-w-[12rem]' },
    { id: 'vs', header: 'vs ScoreLM', cell: (p) => <VsBadge value={p.vs_scorelm} /> },
    { id: 'price_vs', header: 'Price vs ScoreLM', cell: (p) => <PriceVsBadge value={p.price_vs_scorelm} /> },
    { id: 'public', header: 'Public pages', cell: (p) => <span className="text-muted">{PUBLIC_PAGES_LABELS[p.public_league_pages]}</span> },
    { id: 'target', header: 'Target', cell: (p) => (p.is_target ? <Badge tone="accent">Target</Badge> : <span className="text-subtle">—</span>) },
    { id: 'leads', header: 'Leads', align: 'right', cell: (p) => <span className="font-mono text-xs tabular">{p.lead_count}</span> },
  ]

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader title="Platforms" description="What leagues use today, what it costs, and how it compares with ScoreLM." actions={<LoadButton />} />
      <BaselineCard />

      <div className="grid gap-3">
        {/* One toolbar (owner, 2026-11-03): the bucket is a select with counts, not a row of ten chips. */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <SelectInput
            aria-label="Filter by bucket"
            value={bucket ?? ''}
            options={[
              { value: '', label: counts ? `Any bucket (${counts.all})` : 'Any bucket' },
              ...BUCKETS.map((b) => ({ value: b, label: counts ? `${BUCKET_LABELS[b]} (${counts[b]})` : BUCKET_LABELS[b] })),
            ]}
            onChange={(e) => update({ bucket: e.target.value || undefined })}
            className="h-8 sm:w-52"
          />
          <SelectInput aria-label="Filter by kind" value={kind ?? ''} options={KIND_FILTERS} onChange={(e) => update({ kind: e.target.value })} className="h-8 sm:w-44" />
          <SelectInput aria-label="Filter by target" value={target === undefined ? '' : String(target)} options={TARGET_FILTERS} onChange={(e) => update({ target: e.target.value })} className="h-8 sm:w-36" />
          <SelectInput aria-label="Filter by public pages" value={public_pages ?? ''} options={PUBLIC_FILTERS} onChange={(e) => update({ public_pages: e.target.value })} className="col-span-2 h-8 sm:col-span-1 sm:w-44" />
        </div>
      </div>

      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={5} label="Loading platforms" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load platforms" onRetry={refetch} />
        ) : rows.length === 0 ? (
          hasFilters ? (
            <EmptyState
              title="No platforms match"
              description="Try another filter."
              action={
                <Button size="sm" onClick={() => update({ kind: undefined, bucket: undefined, target: undefined, public_pages: undefined })}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState title="No platforms yet" description="Load the list to see what leagues use, and to link your leads to it." action={<LoadButton variant="primary" />} />
          )
        ) : (
          <DataTable caption="Platforms" rows={rows} columns={columns} rowKey={(p) => p.id} rowHref={(p) => `/growth/platforms/${p.id}`} busy={isFetching && status === 'success'} />
        )}
      </Card>
      <p className="text-xs text-muted">
        A bucket is a suggestion, not a rule: any lead can be emailed. <Link to="/growth/leads" className="underline underline-offset-2 hover:text-accent">See leads by bucket</Link>
      </p>
    </div>
  )
}
